import type { MouseEvent } from "react"
import { useCallback, useMemo } from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"

export type DetailDrawerType = "issue" | "project"

export interface DetailDrawerTarget {
	type: DetailDrawerType
	id: string
}

const SEARCH_PARAM: Record<DetailDrawerType, string> = {
	issue: "issue",
	project: "project",
}

export function detailPath(type: DetailDrawerType, id: string) {
	return type === "issue" ? `/issues/${id}` : `/projects/${id}`
}

export function useDetailDrawerTarget(): DetailDrawerTarget | null {
	const issueId = useSearch({ strict: false, select: (s) => s.issue })
	const projectId = useSearch({ strict: false, select: (s) => s.project })
	return useMemo(() => {
		if (issueId) return { type: "issue", id: issueId }
		if (projectId) return { type: "project", id: projectId }
		return null
	}, [issueId, projectId])
}

export function useDetailDrawer() {
	const navigate = useNavigate()
	const target = useDetailDrawerTarget()

	const open = useCallback(
		(type: DetailDrawerType, id: string) => {
			void navigate({
				to: ".",
				search: (prev) => ({
					...prev,
					[SEARCH_PARAM.issue]: undefined,
					[SEARCH_PARAM.project]: undefined,
					[SEARCH_PARAM[type]]: id,
				}),
				hash: true,
			})
		},
		[navigate],
	)

	const close = useCallback(() => {
		if (!target) return
		void navigate({
			to: ".",
			search: (prev) => ({
				...prev,
				[SEARCH_PARAM.issue]: undefined,
				[SEARCH_PARAM.project]: undefined,
			}),
			hash: true,
			replace: true,
			resetScroll: false,
		})
	}, [navigate, target])

	const expand = useCallback(
		(target: DetailDrawerTarget) => {
			void navigate({ to: detailPath(target.type, target.id) })
		},
		[navigate],
	)

	return { open, close, expand }
}

function opensInNewTab(e: MouseEvent) {
	return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0
}

/**
 * Keeps the real href so cmd-click still opens the full page in a new tab,
 * while a plain click peeks at the record in the side drawer.
 */
export function useDetailLinkFactory() {
	const { open } = useDetailDrawer()

	return useCallback(
		(type: DetailDrawerType, id: string) => ({
			to: detailPath(type, id),
			onClick: (e: MouseEvent) => {
				if (e.defaultPrevented || opensInNewTab(e)) return
				e.preventDefault()
				open(type, id)
			},
		}),
		[open],
	)
}

export function useDetailLinkProps(type: DetailDrawerType, id: string) {
	const linkProps = useDetailLinkFactory()
	return useMemo(() => linkProps(type, id), [id, linkProps, type])
}
